import { useSubscription } from "@apollo/client";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { SUBSCRIPTION_ON_ERROR_LIVE } from "../../../graphql/live/subscription";
import { convertNumberToWords, convertThaiToLao } from "../../../helpers";
import { useSpeechSynthesis } from 'react-speech-kit';
import Swal from "sweetalert2";
import { io } from "socket.io-client";
import { convertTextLaoToThai, isLaoLanguage } from "../../../helpers/textLaosToThai";
import { K_LOCAL_STORAGE_SHOP_SETTING_DATA, SERVER_URI, SOCKET_SERVER_URI, TEXT_SERVICES } from "../../../constants";
import _ from 'lodash';

/**
 * Unique id per announcement, so the server can drop a duplicate instead of speaking it
 * twice. crypto.randomUUID needs a secure context and is absent on older Safari/WebView,
 * which some shops run — hence the fallback.
 */
const makeEventId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

export function LiveErrorScreen({ liveData, playNoProduct }) {
    const soundPlayer = useRef(null);
    const soundPlayerCloud = useRef(null);
    const socketRef = useRef(null);
    const [_voices, setVoices] = useState([]);
    const [setting, setSetting] = useState({});
    // Add this state manual play audio local
    const [pendingAudioUrl, setPendingAudioUrl] = useState(null);
    const audioButtonRef = useRef(null);
    // Add this state manual play audio cloud
    const [pendingAudioUrlCloud, setPendingAudioUrlCloud] = useState(null);
    const audioButtonRefCloud = useRef(null);

    const liveId = liveData?.id;
    const shopId = localStorage.getItem("SHOP");
    const { speak } = useSpeechSynthesis();

    const { data } = useSubscription(SUBSCRIPTION_ON_ERROR_LIVE, {
        variables: {
            liveId: liveData?.id
        }
    });

    // ... existing code ...
    const [audioContext, setAudioContext] = useState(null);
    
    // Initialize audio context on component mount
    useEffect(() => {
        // Create audio context
        const context = new (window.AudioContext || window.webkitAudioContext)();
        setAudioContext(context);
        
        // Resume context on user interaction
        const resumeAudioContext = () => {
            if (context.state === 'suspended') {
                context.resume();
            }
        };
        
        // Add event listeners for user interaction
        document.addEventListener('click', resumeAudioContext);
        document.addEventListener('touchstart', resumeAudioContext);
        document.addEventListener('keydown', resumeAudioContext);

        function setSpeechVoices() {
            setVoices(window.speechSynthesis.getVoices());
        }

        setSpeechVoices();
        if (speechSynthesis.onvoiceschanged !== undefined) {
            speechSynthesis.onvoiceschanged = setSpeechVoices;
        }

        return () => {
            // Clean up event listeners
            document.removeEventListener('click', resumeAudioContext);
            document.removeEventListener('touchstart', resumeAudioContext);
            document.removeEventListener('keydown', resumeAudioContext);
            
            // Close audio context
            if (context) {
                context.close();
            }
        };
    }, []);

    useEffect(() => {
        if (!setting?.id) {
            const settingData = localStorage.getItem(K_LOCAL_STORAGE_SHOP_SETTING_DATA);
            if (settingData) {
                setSetting(JSON.parse(settingData));
            }
        }
    }, [setting]);

    useEffect(() => {
        if (data) {
            if (data.onErrorLive.code === "LIVE_AMOUNT_IS_LESS_THAN_0") {
                if (playNoProduct){
                    // If use cloud text to speech
                    if (setting?.isLiveSiriVoice === true){
                        console.log("use cloud text to speech");
                        notifyStockEmpty(data?.onErrorLive?.data);
                    } else {
                        const thaiVoice = _voices.find(voice => voice.lang === 'th-TH');
                        if (!thaiVoice) {
                            // check text to speech cloud
                            console.log("check text to speech cloud");
                            notifyStockEmpty(data?.onErrorLive?.data);
                        } else {
                            console.log("check text to speech local");
                            supSpeak();
                        }
                    }
                    // supSpeak();
                }
                Swal.fire({
                    position: "center",
                    icon: "warning",
                    title: `ລະຫັດ: ${data?.onErrorLive?.data} ຫມົດແລ້ວ`,
                    showConfirmButton: false,
                    timer: 5800
                });
            }
        }
        return () => {}
    }, [data]);

    // Extract the error data from props
    const errorData = data?.onErrorLive?.data;

    // Store audio play request instead of playing directly
    const supSpeak = useCallback(() => {
        const thaiVoice = _voices.find(voice => voice.lang === 'th-TH');

        if (!thaiVoice) {
            setPendingAudioUrl('/product_empty.ogg');
            
            // Try to automatically click the button
            setTimeout(() => {
                if (audioButtonRef.current) {
                    audioButtonRef.current.click();
                }
            }, 100);
            return;
        }

        if (!errorData) {
            console.warn('No error data available for text-to-speech');
            return;
        }

        const thaiText = convertThaiToLao(errorData);
        // Get saved rate or use default
        const savedRate = parseFloat(localStorage.getItem('speechRate')) || 0.9;
        let responseText = `รหัส ${convertNumberToWords(thaiText)} หมดแล้วค่ะ`;
        speak({ text: responseText, rate: savedRate, voice: thaiVoice });
    }, [errorData, _voices, speak, setPendingAudioUrl]);

    const closeSocket = useCallback(() => {
        if (socketRef.current) {
            console.log("Closing socket connection.");
            socketRef.current.disconnect();
        }
    }, []);

    /**
     * Initialize the socket once on mount, and KEEP IT OPEN for the whole live session.
     *
     * This used to connect on demand and disconnect again after every announcement, so each
     * sold-out code paid for a fresh handshake. Behind the NLB, which picks an ECS task per
     * TCP connection and has no stickiness, that was a fresh gamble every time.
     *
     * websocket only, no upgrade, no polling fallback. Polling keeps its session id in one
     * task's memory and needs every request to land on that same task — it cannot work behind
     * this load balancer with more than one task. The old `connect_error` handler forced
     * clients onto exactly that broken transport; it is gone.
     */
    useEffect(() => {
        socketRef.current = io(SOCKET_SERVER_URI, {
            autoConnect: false,
            transports: ['websocket'],
            upgrade: false,
            timeout: 10000,
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
        });

        socketRef.current.on('connect_error', (err) => {
            // Log only. Reconnection is socket.io's job, and any announcement that happens
            // while we are down falls back to local speech rather than being queued.
            console.warn('Socket connect_error:', err?.message);
        });

        socketRef.current.connect();

        return () => {
            if (socketRef.current) {
                console.log("Component unmounting, disconnecting socket.");
                closeSocket();
                socketRef.current = null;
            }
        };
    }, [closeSocket]);

    // Manage socket event listeners
    useEffect(() => {
        if (!socketRef.current) return;

        const onConnect = () => {
            // Re-join on EVERY connect, not just the first. A reconnect produces a brand new
            // socket on the server with a new id and no room membership, so without this the
            // page stays connected but silently stops receiving tts-audio.
            socketRef.current.emit("join-live", liveId);
        };

        /**
         * Log only — deliberately NOT supSpeak().
         *
         * This handler used to call supSpeak(), which speaks `errorData`: whatever the last
         * sold-out code was. connect_error fires on every failed reconnection attempt, so a
         * broken transport made the browser repeat that same old code again and again, on
         * its own, without the server involved at all. Local fallback now happens once, at
         * the point an announcement actually fails, in notifyStockEmpty.
         */
        const onConnectError = (error) => {
            console.warn('Socket connection error:', error?.message);
        };

        const onDisconnect = (reason) => {
            console.log('Socket disconnected:', reason);
        };

        const onError = (error) => {
            console.error('Socket error:', error);
        };

        const handleTtsAudio = (data) => {
            // console.log("Received TTS audio data: ", data);
            if (data?.status && data?.liveId === liveId && data?.services === TEXT_SERVICES[0]) {
                try {
                    if (!data?.audioContent) {
                        console.log("No audio content received");
                        supSpeak();
                        return;
                    }

                    if (audioContext && audioContext.state === 'running') {
                        const base64 = data.audioContent;
                        const binaryString = window.atob(base64);
                        const len = binaryString.length;
                        const bytes = new Uint8Array(len);
                        for (let i = 0; i < len; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        
                        audioContext.decodeAudioData(bytes.buffer, 
                            (buffer) => {
                                const source = audioContext.createBufferSource();
                                source.buffer = buffer;
                                source.connect(audioContext.destination);
                                source.start(0);
                                console.log('Audio playback started successfully with Web Audio API');
                            },
                            (error) => {
                                console.error('Error decoding audio data:', error);
                                fallbackToHtmlAudio(data.audioContent);
                            }
                        );
                    } else {
                        fallbackToHtmlAudio(data.audioContent);
                    }

                    localStorage.setItem("TEXT_SERVICES", JSON.stringify({cloud: true, service: TEXT_SERVICES[0]}));
                } catch (error) {
                    console.error("Error playing audio:", error);
                    supSpeak();
                }
                // NOTE: no closeSocket() here. Tearing the connection down after every
                // announcement is what forced a new handshake — and a new task lottery —
                // for the next one. The connection now lives as long as the live does.
            } else {
                if (_.isEmpty(data?.services)) {
                    localStorage.setItem("TEXT_SERVICES_ERROR", JSON.stringify(data));
                    supSpeak();
                }
            }
        };

        const fallbackToHtmlAudio = (audioContent) => {
            const audioUrl = `data:audio/mp3;base64,${audioContent}`;
            setPendingAudioUrlCloud(audioUrl);
            if (audioButtonRefCloud.current) {
                audioButtonRefCloud.current.click();
            }
        };

        socketRef.current.on('connect', onConnect);
        socketRef.current.on('connect_error', onConnectError);
        socketRef.current.on('disconnect', onDisconnect);
        socketRef.current.on('error', onError);
        socketRef.current.on("tts-audio", handleTtsAudio);

        return () => {
            if (socketRef.current) {
                socketRef.current.off('connect', onConnect);
                socketRef.current.off('connect_error', onConnectError);
                socketRef.current.off('disconnect', onDisconnect);
                socketRef.current.off('error', onError);
                socketRef.current.off("tts-audio", handleTtsAudio);
            }
        };
    }, [liveId, audioContext, closeSocket, supSpeak]);

    /**
     * Announce that a code has sold out.
     *
     * An announcement is only worth making NOW. If the socket is not connected at this
     * instant we speak locally and drop it — we never queue it for a later connect.
     *
     * The previous version did the opposite: while the socket was CONNECTING it registered
     * another `socketRef.current.once('connect', ...)` per call, each closing over its own
     * text. Every sold-out code during an outage stacked up another callback, and when a
     * connect finally succeeded they all fired at once — the shop heard a burst of codes
     * that had sold out minutes earlier. socket.io's own sendBuffer replayed emits made
     * while disconnected for the same reason. Emitting only when `connected` keeps packets
     * out of that buffer entirely.
     */
    const notifyStockEmpty = (text) => {
        if (!liveId) {
            console.error("Live ID is missing");
            supSpeak();
            return;
        }

        if (!socketRef.current) {
            console.error("Socket not initialized");
            supSpeak();
            return;
        }

        const languageLaos = isLaoLanguage(text);
        if (!languageLaos) console.log("Text language could not be identified.");
        const newText = languageLaos ? convertTextLaoToThai(text) : text;

        // Not connected right now — say it locally instead of saving it up for later.
        if (!socketRef.current.connected) {
            console.warn("Socket not connected, speaking locally instead of queueing.");
            supSpeak();
            return;
        }

        const payload = {
            eventId: makeEventId(),
            text: `รหัส ${newText} หมดแล้วค่ะ`,
            cfCode: text,
            liveId,
            services: "GOOGLE_CLOUD",
            shop: shopId,
            ts: Date.now(),
        };

        // Ack with timeout: if the server does not answer, fall back to local speech rather
        // than leaving the shop in silence wondering whether it was heard.
        socketRef.current.timeout(8000).emit("stock-empty", payload, (err, res) => {
            if (err) {
                console.warn("stock-empty not acknowledged, falling back to local speech");
                supSpeak();
                return;
            }
            if (res && res.status === false && res.message !== "DUPLICATE_EVENT" && res.message !== "STALE_EVENT") {
                console.warn("stock-empty rejected:", res.message);
                supSpeak();
            }
        });
    };

    return (
    <>
        <audio ref={soundPlayer} preload="metadata" />
        <audio ref={soundPlayerCloud} preload="metadata" />

        {/* Hidden play button that can be programmatically clicked */}
        <button 
            ref={audioButtonRef}
            style={{ position: 'fixed', bottom: 0, right: 0, opacity: 0.1, zIndex: 9999 }}
            onClick={() => {
                if (pendingAudioUrl) {
                    soundPlayer.current.src = pendingAudioUrl;
                    soundPlayer.current.load();
                    soundPlayer.current.play()
                        .then(() => {
                            console.log('Audio played successfully via button click');
                            setPendingAudioUrl(null);
                        })
                        .catch(err => {
                            console.error('Audio play failed even with button:', err);
                            supSpeak();
                        });
                }
            }}
        >
        </button>

        {/* Visible play button for user interaction */}
        <button 
            ref={audioButtonRefCloud}
            style={{ position: 'fixed', bottom: 0, right: 0, opacity: 0.1, zIndex: 9999 }}
            onClick={() => {
                if (pendingAudioUrlCloud) {
                    soundPlayerCloud.current.src = pendingAudioUrlCloud;
                    soundPlayerCloud.current.load();
                    soundPlayerCloud.current.play()
                        .then(() => {
                            console.log('Audio played successfully via user click');
                            setPendingAudioUrlCloud(null);
                        })
                        .catch(err => {
                            console.error('Audio play failed even with user click:', err);
                            supSpeak();
                        });
                }
            }}
        >
        </button>
    </>
    );
}